import React from 'react';
import { appName, contactEmail, contactNumber } from '../Constants';
import Footer from '../Footer';
import Navbar from '../Navbar';
import { useMenuItems } from '../menuItems';

const PrivacyPolicy = () => {
    const { menuItems } = useMenuItems();
    return (
        <div className='font-sans antialiased bg-landing-white'>
            <Navbar menuItems={menuItems} />
            <section className='py-24 bg-gradient-to-br from-landing-slate-50 to-landing-white'>
                <div className='max-w-4xl mx-auto px-6'>
                    <div className='text-center mb-16'>
                        <span className='inline-flex items-center gap-2 bg-landing-blue-500/10 border border-landing-blue-500/20 rounded-full px-4 py-2 text-landing-blue-500 font-bold tracking-wide uppercase text-sm'>
                            Legal
                        </span>
                        <h1 className='text-4xl md:text-5xl font-bold text-landing-slate-900 mt-4 mb-6'>
                            Privacy Policy
                        </h1>
                        <p className='text-xl text-landing-slate-600 max-w-2xl mx-auto leading-relaxed'>
                            How we collect, use, and protect your information
                        </p>
                        <p className='text-sm text-landing-slate-500 mt-2'>
                            Last Updated: February 2026
                        </p>
                    </div>

                    <div className='bg-landing-white rounded-2xl p-8 shadow-sm border border-landing-gray-100'>
                        <p className='mb-4'>
                            The following terminology applies to these Terms and
                            Conditions, Privacy Statement and Disclaimer Notice
                            and all Agreements: "Client", "You" and "Your"
                            refers to you, the person log on this website and
                            compliant to the Company's terms and conditions.
                            "The Company", "Ourselves", "We", "Our" and "Us",
                            refers to our Company. "Party", "Parties", or "Us",
                            refers to both the Client and ourselves. This
                            Privacy Policy describes our practices with respect
                            to Personal Information we collect from or about you
                            when you use our website, applications, Microsoft
                            Teams integration, and services (collectively,
                            "Services").
                        </p>
                        <p className='mb-4'>
                            This Privacy Policy does not apply to content that
                            we process on behalf of customers of our business
                            offerings, such as our API. Our use of that data is
                            governed by our customer agreements covering access
                            to and use of those offerings.
                        </p>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Personal Information We Collect
                        </h3>
                        <p className='mb-4'>
                            We collect personal information relating to you
                            ("Personal Information") as follows:
                        </p>

                        <h4 className='text-lg font-semibold mt-4 mb-2'>
                            Personal Information You Provide:
                        </h4>
                        <p className='mb-2'>
                            We collect Personal Information if you create an
                            account to use our Services or communicate with us
                            as follows:
                        </p>
                        <ul className='list-disc list-inside mb-4 space-y-2'>
                            <li>
                                <strong>Account Information:</strong> When you
                                create an account with us, we will collect
                                information associated with your account,
                                including your name, contact information,
                                account credentials, payment card information,
                                and transaction history (collectively, "Account
                                Information").
                            </li>
                            <li>
                                <strong>Microsoft Account Integration:</strong>{' '}
                                When you sign in using Microsoft (via Google
                                OAuth or Microsoft Teams), we collect your
                                Microsoft account information including email,
                                display name, and organization details (tenant
                                ID). We do not store your Microsoft password.
                            </li>
                            <li>
                                <strong>Microsoft Teams Data:</strong> When you
                                use our Microsoft Teams bot, we collect:
                                <ul className='list-circle list-inside ml-6 mt-2 space-y-1'>
                                    <li>
                                        Your Microsoft Teams user profile (name,
                                        email)
                                    </li>
                                    <li>
                                        Your organization's tenant ID (to link
                                        you to your organization)
                                    </li>
                                    <li>
                                        Conversation data (messages sent to the
                                        bot)
                                    </li>
                                    <li>
                                        Usage analytics (queries processed,
                                        response times)
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <strong>User Content:</strong> When you use our
                                Services, we collect Personal Information that
                                is included in the input, file uploads, or
                                feedback that you provide to our Services
                                ("Content"). This includes documents uploaded
                                for processing, queries submitted through our
                                web interface or Microsoft Teams bot, and any
                                feedback you provide.
                            </li>
                            <li>
                                <strong>Communication Information:</strong> If
                                you communicate with us, we collect your name,
                                contact information, and the contents of any
                                messages you send ("Communication Information").
                            </li>
                            <li>
                                <strong>Organization Information:</strong> If
                                you are part of an organization using our
                                Services, we collect information about your
                                organization including organization name,
                                industry, and the number of users.
                            </li>
                            <li>
                                <strong>Other Information You Provide:</strong>{' '}
                                We collect other information that you may
                                provide to us, such as when you participate in
                                our events or surveys or provide us with
                                information to establish your identity
                                (collectively, "Other Information You Provide").
                            </li>
                        </ul>

                        <h4 className='text-lg font-semibold mt-4 mb-2'>
                            Personal Information We Receive Automatically from
                            Your Use of the Services:
                        </h4>
                        <p className='mb-2'>
                            When you visit, use, or interact with the Services,
                            we receive the following information about your
                            visit, use, or interactions ("Technical
                            Information"):
                        </p>
                        <ul className='list-disc list-inside mb-4 space-y-2'>
                            <li>
                                <strong>Log Data:</strong> Information that your
                                browser or device automatically sends when you
                                use our Services. Log data includes your
                                Internet Protocol address, browser type and
                                settings, the date and time of your request, and
                                how you interact with our Services.
                            </li>
                            <li>
                                <strong>Usage Data:</strong> We may
                                automatically collect information about your use
                                of the Services, such as the types of content
                                that you view or engage with, the features you
                                use and the actions you take, as well as your
                                time zone, country, the dates and times of
                                access, user agent and version, type of computer
                                or mobile device, and your computer connection.
                            </li>
                            <li>
                                <strong>
                                    Microsoft Teams Integration Data:
                                </strong>{' '}
                                When using our Microsoft Teams bot, we
                                automatically collect:
                                <ul className='list-circle list-inside ml-6 mt-2 space-y-1'>
                                    <li>Message timestamps and frequency</li>
                                    <li>
                                        Response times and bot performance
                                        metrics
                                    </li>
                                    <li>
                                        Authentication events (login/logout)
                                    </li>
                                    <li>Error logs for troubleshooting</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Device Information:</strong> Includes
                                name of the device, operating system, device
                                identifiers, and browser you are using.
                                Information collected may depend on the type of
                                device you use and its settings.
                            </li>
                            <li>
                                <strong>Cookies:</strong> We use cookies to
                                operate and administer our Services, and improve
                                your experience. A "cookie" is a piece of
                                information sent to your browser by a website
                                you visit. You can set your browser to accept
                                all cookies, to reject all cookies, or to notify
                                you whenever a cookie is offered so that you can
                                decide each time whether to accept it.
                            </li>
                        </ul>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            How We Use Personal Information
                        </h3>
                        <p className='mb-2'>
                            We may use Personal Information for the following
                            purposes:
                        </p>
                        <ul className='list-disc list-inside mb-4 space-y-2'>
                            <li>
                                <strong>
                                    To provide, maintain and improve our
                                    Services:
                                </strong>{' '}
                                Including to provide you with the services,
                                products and information you request; process
                                transactions; provide customer service; and
                                improve our Services.
                            </li>
                            <li>
                                <strong>
                                    Microsoft Teams Bot Functionality:
                                </strong>{' '}
                                To authenticate you via Microsoft SSO, link you
                                to your organization, process your queries,
                                maintain conversation history, and provide
                                personalized responses based on your
                                organization's knowledge base.
                            </li>
                            <li>
                                <strong>Organization Management:</strong> To
                                link you to your organization based on your
                                Microsoft tenant ID, manage organization
                                memberships, and provide organization-specific
                                features and content.
                            </li>
                            <li>
                                <strong>To develop new Services:</strong>{' '}
                                Including to research and develop new services
                                or features.
                            </li>
                            <li>
                                <strong>To send you information:</strong>{' '}
                                Including to send you information about our
                                Services, to provide you with updates and to
                                respond to your requests.
                            </li>
                            <li>
                                <strong>For security and safety:</strong>{' '}
                                Including to detect, prevent and respond to
                                security incidents, and to protect against
                                malicious, deceptive, fraudulent or illegal
                                activity.
                            </li>
                            <li>
                                <strong>
                                    To comply with legal obligations:
                                </strong>{' '}
                                Including to comply with applicable law and
                                legal process, and to respond to lawful requests
                                from public authorities.
                            </li>
                        </ul>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Microsoft Teams Integration
                        </h3>
                        <p className='mb-4'>
                            Our Microsoft Teams integration operates as follows:
                        </p>
                        <ul className='list-disc list-inside mb-4 space-y-2'>
                            <li>
                                <strong>Authentication:</strong> We use
                                Microsoft's OAuth 2.0 protocol to authenticate
                                you. We do not store your Microsoft password.
                                Your Microsoft credentials are managed by
                                Microsoft, not by us.
                            </li>
                            <li>
                                <strong>Organization Linking:</strong> We use
                                your Microsoft tenant ID to automatically link
                                you to your organization in our system. This
                                ensures you have access to your organization's
                                knowledge base and settings.
                            </li>
                            <li>
                                <strong>Data Processing:</strong> Messages you
                                send to our Teams bot are processed to generate
                                responses. These messages may be stored for
                                improving our Services, providing conversation
                                history, and troubleshooting.
                            </li>
                            <li>
                                <strong>Data Retention:</strong> Teams
                                conversation data is retained for as long as
                                your account is active and for a reasonable
                                period thereafter for business purposes.
                            </li>
                            <li>
                                <strong>No Third-Party Sharing:</strong> We do
                                not share your Teams conversation data with
                                third parties except as required by law or as
                                necessary to provide the Services.
                            </li>
                        </ul>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Disclosure of Personal Information
                        </h3>
                        <p className='mb-4'>
                            We may share your Personal Information in the
                            following circumstances:
                        </p>
                        <ul className='list-disc list-inside mb-4 space-y-2'>
                            <li>
                                <strong>Service Providers:</strong> We may share
                                Personal Information with vendors, service
                                providers, and contractors that perform services
                                for us or on our behalf, which may include
                                providing cloud storage, hosting, payment
                                processing, or customer support services.
                            </li>
                            <li>
                                <strong>Organization Members:</strong> If you
                                are part of an organization, your usage data and
                                queries may be visible to your organization's
                                administrators for analytics and management
                                purposes.
                            </li>
                            <li>
                                <strong>Business Transfers:</strong> We may
                                share Personal Information as part of a merger,
                                acquisition, or sale of all or a portion of our
                                business or assets.
                            </li>
                            <li>
                                <strong>Legal Requirements:</strong> We may
                                share Personal Information to comply with
                                applicable laws, regulations, or legal process,
                                to protect our rights, property, or safety, or
                                the rights, property, or safety of our users or
                                others.
                            </li>
                            <li>
                                <strong>With Your Consent:</strong> We may share
                                Personal Information when you give us your
                                consent to do so.
                            </li>
                        </ul>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Your Rights and Choices
                        </h3>
                        <p className='mb-4'>
                            Depending on your location, you may have certain
                            rights regarding your Personal Information:
                        </p>
                        <ul className='list-disc list-inside mb-4 space-y-2'>
                            <li>
                                <strong>Access:</strong> You can request access
                                to the Personal Information we hold about you.
                            </li>
                            <li>
                                <strong>Correction:</strong> You can request
                                that we correct inaccurate Personal Information.
                            </li>
                            <li>
                                <strong>Deletion:</strong> You can request that
                                we delete your Personal Information, subject to
                                certain exceptions.
                            </li>
                            <li>
                                <strong>Portability:</strong> You can request a
                                copy of your Personal Information in a
                                structured, commonly used format.
                            </li>
                            <li>
                                <strong>Objection:</strong> You can object to
                                our processing of your Personal Information in
                                certain circumstances.
                            </li>
                            <li>
                                <strong>
                                    Microsoft Account Disconnection:
                                </strong>{' '}
                                You can disconnect your Microsoft account from{' '}
                                {appName} at any time through your account
                                settings or by signing out from Microsoft Teams.
                            </li>
                        </ul>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Third Party Links
                        </h3>
                        <p className='mb-4'>
                            The Service may contain links to other websites not
                            operated or controlled by {appName}, including
                            social media services and Microsoft services ("Third
                            Party Sites"). The information that you share with
                            Third Party Sites will be governed by the specific
                            privacy policies and terms of service of the Third
                            Party Sites and not by this Privacy Policy. By
                            providing these links we do not imply that we
                            endorse or have reviewed these sites. Please contact
                            the Third Party Sites directly for information on
                            their privacy practices and policies.
                        </p>
                        <p className='mb-4'>
                            <strong>Microsoft Privacy:</strong> Our Microsoft
                            Teams integration is subject to Microsoft's privacy
                            policies in addition to this Privacy Policy. Please
                            review Microsoft's privacy statement at{' '}
                            <a
                                href='https://privacy.microsoft.com'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-landing-blue-500 hover:underline'
                            >
                                https://privacy.microsoft.com
                            </a>{' '}
                            for information about how Microsoft handles your
                            data.
                        </p>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Security and Retention
                        </h3>
                        <p className='mb-4'>
                            We implement commercially reasonable technical,
                            administrative, and organizational measures to
                            protect Personal Information both online and offline
                            from loss, misuse, and unauthorized access,
                            disclosure, alteration, or destruction. However, no
                            Internet or email transmission is ever fully secure
                            or error free. In particular, email sent to or from
                            us may not be secure. Therefore, you should take
                            special care in deciding what information you send
                            to us via the Service or email. In addition, we are
                            not responsible for circumvention of any privacy
                            settings or security measures contained on the
                            Service, or third-party websites.
                        </p>
                        <p className='mb-4'>
                            We'll retain your Personal Information for only as
                            long as we need in order to provide our Service to
                            you, or for other legitimate business purposes such
                            as resolving disputes, safety and security reasons,
                            or complying with our legal obligations under Indian
                            law. How long we retain Personal Information will
                            depend on a number of factors, such as the amount,
                            nature, and sensitivity of the information, the
                            potential risk of harm from unauthorized use or
                            disclosure, our purpose for processing the
                            information, and any legal requirements.
                        </p>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            International Users
                        </h3>
                        <p className='mb-4'>
                            By using our Service, you understand and acknowledge
                            that your Personal Information will be processed and
                            stored in our facilities and servers in India and
                            may be disclosed to our service providers and
                            affiliates in other jurisdictions. We are an Indian
                            company and operate primarily under Indian data
                            protection laws.
                        </p>
                        <p className='mb-4'>
                            <strong>Legal basis for processing:</strong> Our
                            legal bases for processing your Personal Information
                            include:
                        </p>
                        <ul className='list-disc pl-5 mb-4 space-y-2'>
                            <li>
                                <strong>Performance of a contract:</strong> When
                                we provide and maintain our Services. When we
                                process Account Information, Content, and
                                Technical Information solely to provide our
                                Services to you, this information is necessary
                                to be able to provide our Services. If you do
                                not provide this information, we may not be able
                                to provide our Services to you.
                            </li>
                            <li>
                                <strong>Legitimate interests:</strong> When we
                                process your information for our legitimate
                                business interests, such as improving our
                                Services, conducting analytics, and ensuring
                                security.
                            </li>
                            <li>
                                <strong>Consent:</strong> When you have given us
                                consent to process your Personal Information for
                                specific purposes, such as Microsoft Teams
                                integration.
                            </li>
                            <li>
                                <strong>
                                    Compliance with legal obligations:
                                </strong>{' '}
                                When we use your Personal Information to comply
                                with applicable Indian law or when we protect
                                our or our affiliates', users', or third
                                parties' rights, safety, and property.
                            </li>
                        </ul>
                        <p className='mb-4'>
                            <strong>Data transfers:</strong> Where required, we
                            will use appropriate safeguards for transferring
                            Personal Information outside of India and other
                            countries. We will only transfer Personal
                            Information pursuant to a legally valid transfer
                            mechanism in accordance with the Information
                            Technology Act, 2000 and applicable rules.
                        </p>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Children's Privacy
                        </h3>
                        <p className='mb-4'>
                            Our Services are not directed to children under the
                            age of 18. We do not knowingly collect Personal
                            Information from children under 18. If you believe
                            that a child has provided Personal Information to
                            us, please contact us and we will delete such
                            information.
                        </p>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            Changes to the Privacy Policy
                        </h3>
                        <p className='mb-4'>
                            We may update this Privacy Policy from time to time.
                            When we do, we will post an updated version on this
                            page, unless another type of notice is required by
                            applicable law. We will notify you of material
                            changes via email or through the Services at least
                            30 days before they take effect.
                        </p>

                        <h3 className='text-xl font-semibold mt-6 mb-3'>
                            How to Contact Us
                        </h3>
                        <p className='mb-4'>
                            Please contact us if you have any questions or
                            concerns not already addressed in this Privacy
                            Policy.
                        </p>
                        {contactEmail ? (
                            <div className='mt-4'>
                                <p className='font-medium'>Email:</p>
                                <a
                                    href={`mailto:${contactEmail}`}
                                    className='text-landing-blue-600 hover:underline'
                                >
                                    {contactEmail}
                                </a>
                            </div>
                        ) : null}
                        {contactNumber ? (
                            <div className='mt-2'>
                                <p className='font-medium'>Phone:</p>
                                <p className='text-landing-gray-700'>
                                    {contactNumber}
                                </p>
                            </div>
                        ) : null}
                        <div className='mt-4'>
                            <p className='font-medium'>Registered Address:</p>
                            <p className='text-landing-gray-700'>India</p>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
